import { BehaviorSubject, distinctUntilChanged, map } from 'rxjs';
import { v4 } from 'uuid';

import {
  FetchParams,
  Middleware,
  RequestContext,
  ResponseContext,
} from '@neptune/shared/core-apis-middleware-domain';

import { ConnectivityServiceType } from './connectivity-service';
import { NO_LIMITS_BUCKET, RateLimiter } from './rate-limiter';
import { RateLimiterChannel } from './rate-limiter-channel';

export const RATE_LIMITER_STATE = {
  SLOW: 'slow',
  NORMAL: 'normal',
  ON_HOLD: 'on-hold',
} as const;

export type RateLimiterState =
  | {
      mode: typeof RATE_LIMITER_STATE.NORMAL;
      releaseTime: 0;
    }
  | {
      mode: typeof RATE_LIMITER_STATE.SLOW;
      releaseTime: 0;
    }
  | {
      mode: typeof RATE_LIMITER_STATE.ON_HOLD;
      releaseTime: number;
    };

const RATE_LIMITER_DEFAULT_LIMIT_KEY = 'neptune-rld';

export /* lib local */ class RateLimiterService implements ConnectivityServiceType {
  private lockTimeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
  private slowdownTimeoutId: ReturnType<typeof setTimeout> | undefined = undefined;

  private state$ = new BehaviorSubject<RateLimiterState>({
    mode: RATE_LIMITER_STATE.NORMAL,
    releaseTime: 0,
  });

  private lockPromise = Promise.resolve();

  constructor(
    private headerName: string,
    private rateLimiter: RateLimiter,
    private channel: RateLimiterChannel,
  ) {
    this.channel.subscribe(this.rateLimiter.updateBuckets.bind(this.rateLimiter));

    window.addEventListener('blur', () => {
      localStorage.setItem(RATE_LIMITER_DEFAULT_LIMIT_KEY, this.rateLimiter.bottleneckSerialized);
    });
  }

  get promise() {
    return this.lockPromise;
  }

  get asObservable() {
    return this.state$.pipe(distinctUntilChanged());
  }

  get state() {
    return this.state$.pipe(
      map((state) => state.mode),
      distinctUntilChanged(),
    );
  }

  get isLocked() {
    return this.state$.value.mode === RATE_LIMITER_STATE.ON_HOLD;
  }

  private acquireLock(retryAfter: number) {
    if (retryAfter <= 0) {
      return;
    }

    const releaseTime = Date.now() + retryAfter;
    const releaseScheduledAt = this.state$.value.releaseTime;

    if (releaseScheduledAt >= releaseTime) {
      return;
    }

    this.state$.next({ mode: RATE_LIMITER_STATE.ON_HOLD, releaseTime });

    this.scheduleLockRelease(retryAfter);
    this.rateLimiter.restart(retryAfter);
  }

  private setRateLimiterPace() {
    if (this.slowdownTimeoutId) {
      clearTimeout(this.slowdownTimeoutId);
      this.slowdownTimeoutId = undefined;
    }

    if (this.rateLimiter.hasQueuedRequests()) {
      this.state$.next({
        mode: RATE_LIMITER_STATE.SLOW,
        releaseTime: 0,
      });
      this.slowdownTimeoutId = setTimeout(() => this.setRateLimiterPace(), 30_000);
    } else {
      this.state$.next({
        mode: RATE_LIMITER_STATE.NORMAL,
        releaseTime: 0,
      });
    }
  }

  private scheduleLockRelease(retryAfter: number) {
    if (this.lockTimeoutId) {
      clearTimeout(this.lockTimeoutId);
      this.lockTimeoutId = undefined;
    }

    this.lockTimeoutId = setTimeout(() => {
      this.state$.next({
        mode: RATE_LIMITER_STATE.NORMAL,
        releaseTime: 0,
      });
    }, retryAfter);
  }

  private parseRateLimitLimit(headers?: Headers) {
    const header = headers?.get('RateLimit-Limit');

    if (!header) {
      return [];
    }

    return header.split(',').map((part) => {
      const [limit, ...params] = part.trim().split(';');
      const result: Record<string, number> = { value: Number(limit) };

      params.forEach((param) => {
        const [key, value] = param.split('=');
        result[key.trim()] = Number(value);
      });

      return result;
    });
  }

  private getRetryAfter(headers?: Headers) {
    return 1_000 * parseFloat(headers?.get(this.headerName) ?? '5');
  }

  private rateLimit(headers?: Headers) {
    const limit = this.parseRateLimitLimit(headers);

    const reset = headers?.get('RateLimit-Reset');
    const scope = headers?.get('RateLimit-Scope');
    const remaining = headers?.get('RateLimit-Remaining');

    if (limit.length === 0 || reset == null || remaining == null) {
      return undefined;
    }

    return {
      limit: limit[0].value,
      scope: scope || 'global',
      reset: parseFloat(reset) * 1_000,
      remaining: parseFloat(remaining),
    };
  }

  asOpenApiMiddleware(): Middleware {
    return {
      pre: async (context: RequestContext): Promise<FetchParams | void> => {
        const id = v4();
        const path = new URL(context.url).pathname;
        const bucketName = this.rateLimiter.getBucketNameForPath(path);

        (context.init as any).__id = id;
        (context.init as any).__bucketName = bucketName;

        if (this.rateLimiter.canProcessAtNormalPace(bucketName)) {
          this.rateLimiter.processRequest(bucketName, id);
          return;
        }

        const lock = this.rateLimiter.queueRequest(bucketName, id, this.isLocked);
        this.setRateLimiterPace();
        await lock;

        return;
      },
      post: async (context: ResponseContext): Promise<Response | void> => {
        const id = (context.init as any).__id;
        const headers = context.response?.headers;
        const path = new URL(context.url).pathname;
        const reqBucketName = (context.init as any).__bucketName;

        const rateLimit = this.rateLimit(headers);

        if (context.response?.status === 429) {
          this.acquireLock(this.getRetryAfter(headers));

          this.rateLimiter.resolveRequest(reqBucketName, id);

          return;
        }

        if (rateLimit) {
          const bucketName = rateLimit.scope === 'global' ? 'global' : path;

          this.rateLimiter.updateBucket(bucketName, rateLimit);

          if (this.rateLimiter.isHittingRateLimit(bucketName)) {
            this.channel.publish(this.rateLimiter.buckets);
          }
        } else {
          this.rateLimiter.updateBucket(path, { ...NO_LIMITS_BUCKET });
        }

        this.rateLimiter.resolveRequest(reqBucketName, id);
      },
    };
  }
}

export const rateLimiterService = new RateLimiterService(
  'Retry-After',
  new RateLimiter(localStorage.getItem(RATE_LIMITER_DEFAULT_LIMIT_KEY) ?? ''),
  new RateLimiterChannel('rate-limiter-channel'),
);
